import { useCallback, useEffect } from 'react';
import {
  matchPath,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { MapRef, useMapInstance } from 'shared/map-exports';
import { LatLng } from 'shared/types';
import { usePrevious } from 'hooks/usePrevious';
import { getMapZoomInteger } from 'features/mapping/mapping.utils';
import { useMapZoomInteger } from 'hooks/useMapZoomInteger';
import { useAuthState } from 'state/useAuthState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { pickMapSearchParams } from 'features/mapping/utils/pickMapSearchParams';
import {
  COORDINATE_DISPLAY_FRACTION_DIGITS,
  MAP_PIN_ROUTE_MATCH_PATHS,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
} from '../../../constants';
import { useLatLngFromUrl } from './useLatLngFromUrl';

type SetSelectedMapPinOptions = {
  /**
   * Replace the current history entry instead of pushing a new one.
   */
  replace?: boolean;
};

type UseSetMapPin = {
  setSelectedMapPin: (
    coords: LatLng,
    options?: SetSelectedMapPinOptions,
  ) => void;
};

const splitUrlIntoPathAndCoords = (
  url: string,
): { pathName: string; coordsString: string } => {
  const [pathName, coordsString = ''] = url.split('@');
  return { pathName, coordsString };
};

export const buildCoordinatesUrlParam = (
  pathName: string,
  coords: LatLng,
  map?: MapRef,
): string => {
  const lat = coords.lat.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS);
  const lng = coords.lng.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS);
  const zoom = getMapZoomInteger(map);
  if (zoom) {
    return `${pathName}@${lat},${lng},${zoom}z`;
  }
  return `${pathName}@${lat},${lng}`;
};

const adjustZoomLevel = (locationPathname: string, newZoom: number): string => {
  const { pathName, coordsString } =
    splitUrlIntoPathAndCoords(locationPathname);
  // Keep any route segment after the coordinates (e.g. `/pin`) so syncing the
  // zoom doesn't drop the pin.
  const slashIndex = coordsString.indexOf('/');
  const coords =
    slashIndex === -1 ? coordsString : coordsString.slice(0, slashIndex);
  const suffix = slashIndex === -1 ? '' : coordsString.slice(slashIndex);
  const baseCoords = coords.replace(/,\d+z?$/, '');
  return `${pathName}@${baseCoords},${newZoom}z${suffix}`;
};

// Syncs URL-based map pin selection with map zoom level.
export const useSyncMapPin = (): void => {
  const history = useHistory();
  const { latitudeParam, longitudeParam } = useLatLngFromUrl();
  const routeMatch = useRouteMatch({ path: ['/@:lat,:lng', '*/@:lat,:lng'] });
  const currentLocationUrl = routeMatch?.url;
  const mapZoom = useMapZoomInteger();
  const prevMapZoom = usePrevious(mapZoom);

  const location = useLocation();

  // Sync URL with the map's zoom level if the URL has lat/lng params
  useEffect(() => {
    if (
      latitudeParam &&
      longitudeParam &&
      prevMapZoom !== mapZoom &&
      mapZoom &&
      currentLocationUrl
    ) {
      // Rebuild from the full pathname (not routeMatch.url, which omits the
      // `/pin` suffix) so a dropped pin survives the zoom sync.
      const updatedPathname = adjustZoomLevel(location.pathname, mapZoom);
      if (updatedPathname === location.pathname) {
        return;
      }
      history.replace({
        pathname: updatedPathname,
        search: location.search,
      });
    }
  }, [
    history,
    location.pathname,
    location.search,
    currentLocationUrl,
    mapZoom,
    prevMapZoom,
    latitudeParam,
    longitudeParam,
  ]);
};

export const resolveMapPinBasePath = (pathname: string): string => {
  if (matchPath(pathname, { path: WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH })) {
    return `${pathname.split('/@')[0]}/`;
  }
  return '/';
};

/**
 * A custom hook for selecting a map pin.
 * `setSelectedMapPin` updates the browser's URL to include:
 * - The selected map pin's coordinates
 * - The current zoom level (if available)
 * - a `/pin` route suffix to trigger showing the pin (home map only)
 */
export const useSetMapPin = (): UseSetMapPin => {
  const history = useHistory();
  const map = useMapInstance();
  const location = useLocation();
  const { restoreEntityDrawer } = useMapEntityDrawer();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  const setSelectedMapPin = useCallback(
    (coords: LatLng, options?: SetSelectedMapPinOptions): void => {
      const basePath = resolveMapPinBasePath(location.pathname);
      const coordinatesPath = buildCoordinatesUrlParam(basePath, coords, map);

      // Coordinates alone are a plain viewport; a dropped pin is marked with a
      // `/pin` route suffix (both on the home map and the progression map).
      const pathname = pinImprovements
        ? `${coordinatesPath}/pin`
        : coordinatesPath;

      // Moving an existing pin keeps its params (e.g. ?tab=). Dropping a pin
      // from anywhere else leaves that entity's drawer params behind.
      const isOnPin = pinImprovements
        ? Boolean(
            matchPath(location.pathname, {
              path: MAP_PIN_ROUTE_MATCH_PATHS,
              exact: true,
            }),
          )
        : location.pathname.includes('/@');

      const search = isOnPin
        ? location.search
        : pickMapSearchParams(location.search);

      const target = { pathname, search };

      if (options?.replace) {
        history.replace(target);
      } else {
        history.push(target);
      }
      restoreEntityDrawer();
    },
    [
      history,
      location.pathname,
      location.search,
      map,
      restoreEntityDrawer,
      pinImprovements,
    ],
  );

  return { setSelectedMapPin };
};

export const useMapPinEnabled = (): boolean => {
  const {
    showMembershipProFeatures,
    permissions: { isInternalUser },
  } = useAuthState();

  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  if (pinImprovements) {
    return true;
  }

  return showMembershipProFeatures || isInternalUser;
};
